import { addMonths, format } from "date-fns";
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useFormatting } from "@/hooks/useFormatting";
import { useTerminology } from "@/hooks/useTerminology";

interface WinCustomerWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: (eventData: any) => void;
}

export function WinCustomerWizard({
  open,
  onOpenChange,
  onComplete,
}: WinCustomerWizardProps) {
  const { getCashMovementTerms, getScenarioTerm } = useTerminology();
  const { formatCurrency } = useFormatting();
  const [step, setStep] = useState(1);

  // Step 1: Basic Information
  const [customerName, setCustomerName] = useState("");
  const [contractValue, setContractValue] = useState("50000");
  const [paymentTerms, setPaymentTerms] = useState("30");
  const [startDate, setStartDate] = useState<Date>(new Date(2026, 1, 1));

  // Step 2: Costs
  const [includeSetupCosts, setIncludeSetupCosts] = useState(true);
  const [setupCost, setSetupCost] = useState("5000");
  const [includeDeliveryCosts, setIncludeDeliveryCosts] = useState(true);
  const [deliveryCostType, setDeliveryCostType] = useState<
    "percentage" | "flat"
  >("percentage");
  const [deliveryCostPercentage, setDeliveryCostPercentage] = useState("30");
  const [deliveryCostFlat, setDeliveryCostFlat] = useState("5000");

  // Step 3: Revenue Schedule
  const [revenueFrequency, setRevenueFrequency] = useState<
    "monthly" | "quarterly"
  >("monthly");
  const [contractDuration, setContractDuration] = useState("12");

  // Step 4: Review
  const [description, setDescription] = useState("");

  const handleNext = () => setStep(step + 1);
  const handleBack = () => setStep(step - 1);

  const handleComplete = () => {
    const eventData = {
      type: "win_customer",
      customerName,
      contractValue: parseFloat(contractValue),
      paymentTerms: parseInt(paymentTerms),
      startDate,
      includeSetupCosts,
      setupCost: includeSetupCosts ? parseFloat(setupCost) : 0,
      includeDeliveryCosts,
      deliveryCostType,
      deliveryCostPercentage:
        deliveryCostType === "percentage"
          ? parseFloat(deliveryCostPercentage)
          : 0,
      deliveryCostFlat:
        deliveryCostType === "flat" ? parseFloat(deliveryCostFlat) : 0,
      revenueFrequency,
      contractDuration: parseInt(contractDuration),
      description,
    };
    onComplete(eventData);
    onOpenChange(false);
    resetForm();
  };

  const resetForm = () => {
    setStep(1);
    setCustomerName("");
    setContractValue("50000");
    setPaymentTerms("30");
    setStartDate(new Date(2026, 1, 1));
    setIncludeSetupCosts(true);
    setSetupCost("5000");
    setIncludeDeliveryCosts(true);
    setDeliveryCostType("percentage");
    setDeliveryCostPercentage("30");
    setDeliveryCostFlat("5000");
    setRevenueFrequency("monthly");
    setContractDuration("12");
    setDescription("");
  };

  const calculateMonthlyRevenue = () => {
    const total = parseFloat(contractValue) || 0;
    const duration = parseInt(contractDuration) || 12;
    if (revenueFrequency === "monthly") {
      return total / duration;
    } else {
      return total / (duration / 3);
    }
  };

  const calculateDeliveryCost = () => {
    if (!includeDeliveryCosts) return 0;
    const monthlyRev = calculateMonthlyRevenue();
    if (deliveryCostType === "percentage") {
      return (monthlyRev * parseFloat(deliveryCostPercentage)) / 100;
    } else {
      return parseFloat(deliveryCostFlat) || 0;
    }
  };

  const calculate12MonthImpact = () => {
    const monthlyRev = calculateMonthlyRevenue();
    const monthlyCost = calculateDeliveryCost();
    const setupCostTotal = includeSetupCosts ? parseFloat(setupCost) || 0 : 0;
    const duration = Math.min(12, parseInt(contractDuration) || 12);

    const totalRevenue = monthlyRev * duration;
    const totalCosts = setupCostTotal + monthlyCost * duration;
    const netImpact = totalRevenue - totalCosts;

    return {
      totalRevenue,
      totalCosts,
      netImpact,
      setupCostTotal,
      monthlyRev,
      monthlyCost,
    };
  };

  const calculateBreakEven = () => {
    const { setupCostTotal, monthlyRev, monthlyCost } =
      calculate12MonthImpact();
    const monthlyNet = monthlyRev - monthlyCost;
    if (monthlyNet <= 0) return null;
    const months = Math.ceil(setupCostTotal / monthlyNet);
    return months;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Win New Customer Event - Step {step} of 4</DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Basic Information</h3>

            <div className="space-y-2">
              <Label htmlFor="customerName">Customer Name *</Label>
              <Input
                id="customerName"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="e.g., BigCo Ltd"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contractValue">Contract Value *</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm">£</span>
                <Input
                  id="contractValue"
                  type="number"
                  value={contractValue}
                  onChange={(e) => setContractValue(e.target.value)}
                />
                <span className="text-sm text-muted-foreground">total</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="paymentTerms">Payment Terms *</Label>
              <Select value={paymentTerms} onValueChange={setPaymentTerms}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Immediate (0 days)</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Start Date *</Label>
              <DatePicker
                value={startDate}
                onChange={(date) => date && setStartDate(date)}
                dateFormat="PPP"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleNext}
                disabled={!customerName || !contractValue}
              >
                Next: Costs →
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Associated Costs</h3>
            <p className="text-sm text-muted-foreground">
              Select costs to include:
            </p>

            <div className="space-y-4 border rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Checkbox
                  id="setupCosts"
                  checked={includeSetupCosts}
                  onCheckedChange={(checked) =>
                    setIncludeSetupCosts(checked as boolean)
                  }
                />
                <div className="flex-1 space-y-2">
                  <Label htmlFor="setupCosts" className="font-semibold">
                    Setup/Onboarding Costs
                  </Label>
                  {includeSetupCosts && (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">£</span>
                        <Input
                          type="number"
                          value={setupCost}
                          onChange={(e) => setSetupCost(e.target.value)}
                          className="w-32"
                        />
                        <span className="text-sm text-muted-foreground">
                          one-time
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Timing: On start date (
                        {format(startDate, "MMM d, yyyy")})
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4 border rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Checkbox
                  id="deliveryCosts"
                  checked={includeDeliveryCosts}
                  onCheckedChange={(checked) =>
                    setIncludeDeliveryCosts(checked as boolean)
                  }
                />
                <div className="flex-1 space-y-2">
                  <Label htmlFor="deliveryCosts" className="font-semibold">
                    Delivery/COGS Costs
                  </Label>
                  {includeDeliveryCosts && (
                    <>
                      <Select
                        value={deliveryCostType}
                        onValueChange={(val) =>
                          setDeliveryCostType(val as "percentage" | "flat")
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percentage">
                            Percentage of revenue
                          </SelectItem>
                          <SelectItem value="flat">
                            Flat monthly cost
                          </SelectItem>
                        </SelectContent>
                      </Select>

                      {deliveryCostType === "percentage" ? (
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={deliveryCostPercentage}
                            onChange={(e) =>
                              setDeliveryCostPercentage(e.target.value)
                            }
                            className="w-24"
                          />
                          <span className="text-sm">% of revenue</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-sm">£</span>
                          <Input
                            type="number"
                            value={deliveryCostFlat}
                            onChange={(e) =>
                              setDeliveryCostFlat(e.target.value)
                            }
                            className="w-32"
                          />
                          <span className="text-sm text-muted-foreground">
                            per month
                          </span>
                        </div>
                      )}
                      <p className="text-sm text-muted-foreground">
                        Monthly cost:{" "}
                        {formatCurrency(calculateDeliveryCost(), "full")}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleBack}>
                ← Back
              </Button>
              <Button onClick={handleNext}>Next: Revenue →</Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Revenue Schedule</h3>

            <div className="space-y-2">
              <Label>Revenue Frequency</Label>
              <Select
                value={revenueFrequency}
                onValueChange={(val) =>
                  setRevenueFrequency(val as "monthly" | "quarterly")
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="contractDuration">
                Contract Duration (months)
              </Label>
              <Input
                id="contractDuration"
                type="number"
                value={contractDuration}
                onChange={(e) => setContractDuration(e.target.value)}
              />
            </div>

            <div className="bg-muted p-4 rounded-lg space-y-2">
              <p className="font-semibold">Revenue Breakdown:</p>
              <p className="text-sm">
                Total contract value:{" "}
                {formatCurrency(parseFloat(contractValue), "full")}
              </p>
              <p className="text-sm">
                {revenueFrequency === "monthly" ? "Monthly" : "Quarterly"}{" "}
                revenue: {formatCurrency(calculateMonthlyRevenue(), "full")}
              </p>
              <p className="text-sm">
                First payment:{" "}
                {format(
                  addMonths(startDate, parseInt(paymentTerms) / 30),
                  "MMM d, yyyy",
                )}
              </p>
              <p className="text-sm text-muted-foreground">
                (Payment terms: {paymentTerms} days after invoice)
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleBack}>
                ← Back
              </Button>
              <Button onClick={handleNext}>Next: Review →</Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Review Event Summary</h3>

            <div className="space-y-2">
              <p className="font-semibold">New Customer: {customerName}</p>
              <p className="text-sm">
                Contract Value:{" "}
                {formatCurrency(parseFloat(contractValue), "full")}
              </p>
              <p className="text-sm">
                Starting: {format(startDate, "MMM d, yyyy")}
              </p>
            </div>

            <div className="border-t pt-4 space-y-3">
              <p className="font-semibold">
                ─── CASH FLOW IMPACT ───────────────────────────
              </p>

              {includeSetupCosts && (
                <>
                  <p className="font-semibold text-sm">
                    One-Time Costs ({format(startDate, "MMM yyyy")}):
                  </p>
                  <p className="text-sm text-red-600">
                    • Setup/Onboarding: -
                    {formatCurrency(parseFloat(setupCost), "full")}
                  </p>
                  <p className="text-sm">
                    Total: -
                    {formatCurrency(
                      calculate12MonthImpact().setupCostTotal,
                      "full",
                    )}
                  </p>
                </>
              )}

              <p className="font-semibold text-sm mt-4">
                {revenueFrequency === "monthly" ? "Monthly" : "Quarterly"}{" "}
                Revenue ({format(startDate, "MMM yyyy")} onwards):
              </p>
              <p className="text-sm text-green-600">
                • Revenue: +{formatCurrency(calculateMonthlyRevenue(), "full")}/
                {revenueFrequency === "monthly" ? "month" : "quarter"}
              </p>
              {includeDeliveryCosts && (
                <p className="text-sm text-red-600">
                  • Delivery/COGS: -
                  {formatCurrency(calculateDeliveryCost(), "full")}/month
                </p>
              )}
              <p className="text-sm">
                Net {revenueFrequency === "monthly" ? "monthly" : "quarterly"}{" "}
                {getCashMovementTerms().in.toLowerCase()}: +£
                {formatCurrency(
                  calculateMonthlyRevenue() - calculateDeliveryCost(),
                  "full",
                )}
              </p>

              <p className="font-semibold text-sm mt-4">
                ─── NET IMPACT (12 months) ──────────────────────
              </p>
              <p className="text-sm">
                Total revenue: +
                {formatCurrency(calculate12MonthImpact().totalRevenue, "full")}
              </p>
              <p className="text-sm">
                Total costs: -
                {formatCurrency(calculate12MonthImpact().totalCosts, "full")}
              </p>
              <p className="text-sm font-semibold">
                Net 12-month impact:{" "}
                {calculate12MonthImpact().netImpact >= 0 ? "+" : ""}
                {formatCurrency(calculate12MonthImpact().netImpact, "full")}
              </p>

              {calculateBreakEven() && (
                <p className="text-sm">
                  Break-even: Month {calculateBreakEven()} (
                  {format(
                    addMonths(startDate, calculateBreakEven()!),
                    "MMM yyyy",
                  )}
                  )
                </p>
              )}

              {calculate12MonthImpact().netImpact > 0 && (
                <div className="bg-green-50 border border-green-200 rounded p-3 mt-4">
                  <p className="text-sm text-green-800">
                    ✓ Recommendation: This customer will be profitable within 12
                    months
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-2 pt-4">
              <Label htmlFor="description">Event Description (optional):</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g., Major new contract with BigCo Ltd"
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleBack}>
                ← Back
              </Button>
              <Button onClick={handleComplete}>
                Add Event to {getScenarioTerm(true)}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
